import { createClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'

// Get environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Check your .env file.')
}

// Create Supabase client with proper typing and enhanced session handling
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: 'app-session',
    storage: window.localStorage,
    flowType: 'pkce',
    debug: import.meta.env.DEV,
    // Use relative URL for local development to avoid CORS issues
    redirectTo: `${window.location.origin}/auth/callback`
  },
  global: {
    headers: {
      'X-Client-Info': 'aem-marketplace'
    }
  },
  db: {
    schema: 'public'
  }
})

// Test and initialize the connection
export const initializeSupabase = async (): Promise<boolean> => {
  try {
    console.log('Initializing database connection...')
    
    // Test basic query with retry
    const maxRetries = 3
    let lastError = null
    let retryDelay = 1000 // Start with 1 second delay

    for (let i = 0; i < maxRetries; i++) {
      try {
        // First check and refresh session if needed
        const { data: { session }, error: sessionError } = await supabase.auth.getSession()
        
        if (sessionError) {
          console.warn('Session check warning:', sessionError)
          if (session) {
            const { error: refreshError } = await supabase.auth.refreshSession()
            if (refreshError) {
              console.warn('Session refresh failed:', refreshError)
              await supabase.auth.signOut()
            }
          }
        }

        // Use a simple health check query
        const { error: pingError } = await supabase
          .from('profiles')
          .select('count', { count: 'exact', head: true })
          .limit(1)
          .maybeSingle()
        
        if (pingError) {
          throw pingError
        }
        
        // Connection successful
        console.log('✅ Supabase connection established')
        
        if (session) {
          console.log('✅ User authenticated:', session.user.email)
        } else {
          console.log('ℹ️ No active session')
        }
        
        return true

      } catch (error) {
        lastError = error
        console.warn(`Connection attempt ${i + 1} failed:`, error)
        
        if (i < maxRetries - 1) {
          // Exponential backoff
          await new Promise(resolve => setTimeout(resolve, retryDelay))
          retryDelay *= 2 // Double the delay for next retry
          continue
        }
      }
    }

    // All retries failed - provide detailed error information
    console.error('❌ Failed to connect to Supabase after multiple attempts')
    
    if (lastError instanceof Error) {
      if (lastError.message.includes('Failed to fetch')) {
        console.error(`
Connection Error Troubleshooting Guide:
1. Check your internet connection
2. Verify Supabase URL: ${supabaseUrl}
3. Ensure Supabase is running and accessible
4. Check CORS settings in Supabase dashboard
5. If using VPN/proxy, check your settings
`)
      } else if (lastError.message.includes('auth')) {
        console.error('Authentication Error: Please verify your Supabase anon key')
      }
      
      console.error('Detailed error:', lastError)
    }
    
    return false

  } catch (error) {
    console.error('❌ Fatal error initializing Supabase:', error)
    return false
  }
}

// Set up auth state change listener
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
    localStorage.removeItem('app-session')
  } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
    if (session) {
      localStorage.setItem('app-session', JSON.stringify(session))
    }
  }
})

// Initialize connection
initializeSupabase()